import { useState } from 'react';
import { cn } from '@/lib/utils';

/**
 * Shared holdings-table primitives — extracted from the report's HoldingsTable
 * so every positions/holdings table in the app (backtest report, client
 * Holdings, …) renders the exact same logo cell and sortable header. Do not
 * fork these: identical look everywhere is the point.
 */

// Source order is parqet-FIRST for looks: parqet returns clean, uniformly
// framed artwork (prefer SVG, with a high-resolution PNG fallback) — the source the Figma uses — so
// the common names get the prettier logo. logo.dev is kept as the broad-coverage
// FALLBACK: its ticker endpoint resolves names parqet misses (MRVL; and non-US
// listings by their Yahoo-suffixed symbol — Tencent 0700.HK, Samsung 005930.KS,
// SoftBank 9984.T), with `fallback=404` forcing a true miss to walk on rather
// than satisfy <img> with a 200 grey placeholder. FMP is the last CDN, then our
// own monogram. parqet & FMP both 404 cleanly on a genuine miss (that's why the
// old logo.dev→parqet→FMP chain already reached the monogram for MRVL), so
// putting parqet first never freezes the walk. The hyphen share-class variant
// (BRK.B → BRK-B) is supplied by `logoCandidates`, so every source gets it.
// Probed 2026-08: MRVL, 0700.HK, 0939.HK, 9988.HK, 9984.T, 005930.KS resolve.
const LOGO_DEV_TOKEN = 'pk_NKp9yy1hRFS87RNX42JkKA';
// Prefer vector artwork at every display size; raster fallbacks request 512px
// for sharp detail headers and Retina screens. Keep the same source order,
// listing identity, lazy loading and session cache (no bulk prefetch).
// https://developers.parqet.com/docs/assets/logos
// https://www.logo.dev/docs/logo-images/introduction
const LOGO_SOURCES: Array<(symbol: string) => string> = [
  (s) => `https://assets.parqet.com/logos/symbol/${s}?format=svg&size=512`,
  (s) =>
    `https://img.logo.dev/ticker/${s}?token=${LOGO_DEV_TOKEN}&size=256&retina=true&format=png&fallback=404`,
  (s) => `https://financialmodelingprep.com/image-stock/${s}.png`,
];

// ISO MIC → CDN/Yahoo exchange suffix. International listings are indexed by the
// logo CDNs under an exchange-suffixed symbol (Samsung 005930 → 005930.KS,
// Aramco 2222 → 2222.SR), never the bare local code — so without this every
// non-US name walks its candidates, 404s, and drops to the monogram (which,
// for a numeric code, is just a digit in a grey circle). US MICs are
// intentionally absent — their bare symbol resolves. Unmapped MICs fall back to
// the bare symbol (no regression).
const MIC_TO_SUFFIX: Record<string, string> = {
  XKRX: 'KS',
  XKOS: 'KQ',
  XSAU: 'SR',
  XTKS: 'T',
  XOSE: 'T',
  XHKG: 'HK',
  XSHG: 'SS',
  XSSC: 'SS',
  XSHE: 'SZ',
  XTAI: 'TW',
  ROCO: 'TWO',
  XNSE: 'NS',
  XBOM: 'BO',
  XLON: 'L',
  XTSE: 'TO',
  XTSX: 'TO',
  XPAR: 'PA',
  XAMS: 'AS',
  XBRU: 'BR',
  XLIS: 'LS',
  XETR: 'DE',
  XFRA: 'F',
  XMIL: 'MI',
  MTAA: 'MI',
  XMAD: 'MC',
  XMCE: 'MC',
  BMEX: 'MC',
  XSWX: 'SW',
  XVTX: 'SW',
  XSTO: 'ST',
  XHEL: 'HE',
  XOSL: 'OL',
  XCSE: 'CO',
  XASX: 'AX',
  XNZE: 'NZ',
  XSES: 'SI',
  SSME: 'SI',
  XBKK: 'BK',
  XIDX: 'JK',
  XKLS: 'KL',
  BVMF: 'SA',
  XMEX: 'MX',
  XSGO: 'SN',
  XJSE: 'JO',
  XIST: 'IS',
  XATH: 'AT',
  XWAR: 'WA',
  XBUD: 'BD',
  XKAR: 'KA',
  XTAE: 'TA',
  XWBO: 'VI',
  WBAH: 'VI',
};

// The CDNs disagree on dots: share classes are keyed hyphenated (BRK.B → BRK-B
// works, BRK.B 404s) while exchange suffixes keep the dot. For a non-US listing
// the exchange-suffixed Yahoo symbol is what all three sources index (Samsung
// 005930 → 005930.KS; HKEX codes are 4-digit zero-padded, 00700 → 0700.HK), so
// try it FIRST on every source, then the hyphen variant, then the raw symbol —
// covers US + international stocks AND ETFs, with the monogram as last resort.
function logoCandidates(ticker: string, mic?: string | null): Array<string> {
  const base = ticker.split(':')[0];
  const key = mic ? mic.toUpperCase() : '';
  const suffix = key ? MIC_TO_SUFFIX[key] : undefined;
  const symbols: Array<string> = [];
  if (suffix) {
    // HKEX Yahoo symbols are 4-digit zero-padded (00700 → 0700).
    const norm =
      key === 'XHKG' ? base.replace(/^0+/, '').padStart(4, '0') : base;
    symbols.push(`${norm}.${suffix}`);
    if (norm !== base) symbols.push(`${base}.${suffix}`);
  }
  const hyphen = base.replace(/\./g, '-');
  if (hyphen !== base) symbols.push(hyphen);
  symbols.push(base);
  const urls: Array<string> = [];
  for (const source of LOGO_SOURCES)
    for (const s of symbols) urls.push(source(s));
  return urls;
}

/**
 * Which candidate index resolved for a ticker, remembered for the session.
 *
 * Without this, a ticker the CDNs don't carry (MRVL is the standing example)
 * re-walked its whole candidate list on EVERY mount — every sort, every filter,
 * every re-render that remounts the row, in every table it appears in. Each walk
 * is 2-4 requests that are already known to 404, so the browser console fills
 * with `GET .../logos/symbol/MRVL 404 (Not Found)` and real errors get buried.
 *
 * Caching the ANSWER (including "none of them worked", as `EXHAUSTED`) means one
 * probe per ticker per session: the row renders the monogram immediately on
 * every subsequent mount and issues no request at all.
 *
 * Deliberately module-level rather than React state — it outlives the component
 * tree, which is the whole point. Deliberately not persisted either: a logo that
 * was missing today may be added, and a page load is a cheap re-check.
 */
const EXHAUSTED = -1;
const logoResolution = new Map<string, number>();

// Company/ETF logo: tries each candidate URL in turn; when none resolve,
// falls back to a clean monogram (neutral tile + the ticker's initial).
// `shape` is round for table rows, square for detail/lots headers; `size`
// bumps the footprint for those headers.
export function AssetLogo({
  ticker,
  mic,
  shape = 'square',
  size = 'sm',
  className,
}: {
  ticker: string;
  /** Exchange MIC (e.g. "XKRX") — adds exchange-suffixed candidates so non-US
   *  listings (Samsung 005930 → 005930.KS) resolve instead of falling to the
   *  monogram. Omit for US names; the bare symbol resolves. */
  mic?: string | null;
  shape?: 'circle' | 'square';
  size?: 'xs' | 'sm' | 'lg';
  className?: string;
}) {
  const candidates = logoCandidates(ticker, mic);
  // Cache the resolution per (ticker, mic): the same bare code on two exchanges
  // resolves to different candidates, so the key must include the MIC.
  const cacheKey = `${ticker}|${mic ?? ''}`;
  // Start from what this listing already resolved to this session, so a known
  // miss renders the monogram without re-requesting and a known hit skips
  // straight to the source that worked.
  const [sourceIdx, setSourceIdx] = useState(
    () => logoResolution.get(cacheKey) ?? 0,
  );
  const exhausted = sourceIdx === EXHAUSTED || sourceIdx >= candidates.length;

  const onLoaded = () => logoResolution.set(cacheKey, sourceIdx);
  const onFailed = () =>
    setSourceIdx((i) => {
      const next = i + 1;
      if (next >= candidates.length) logoResolution.set(cacheKey, EXHAUSTED);
      return next;
    });

  const sizeCls =
    size === 'lg' ? 'h-10 w-10' : size === 'xs' ? 'h-7 w-7' : 'h-8 w-8';
  const radiusCls = shape === 'square' ? 'rounded-md' : 'rounded-full';

  // Monogram fallback keeps a real tile (neutral fill + hairline border + the
  // ticker's initial) since there's no artwork to carry it.
  if (exhausted) {
    return (
      <div
        className={cn(
          'flex shrink-0 items-center justify-center bg-muted border border-border/60',
          sizeCls,
          radiusCls,
          className,
        )}
      >
        <span
          className={cn(
            'font-semibold leading-none text-foreground/70',
            size === 'xs' ? 'text-[10px]' : 'text-sm',
          )}
        >
          {ticker.charAt(0).toUpperCase()}
        </span>
      </div>
    );
  }

  // Resolved logo: the image IS the tile — no wrapper, no border ring (which
  // read as a double border against the marks' own baked-in frame). object-
  // contain + padding on a white field fits the mark without cropping. Matches
  // the Figma treatment.
  //
  // loading="lazy" is load-bearing for big tables: the holdings/constituents
  // tables aren't virtualized, so a "Show: All" of ~490 rows would otherwise
  // fetch ~490 logos at once — and every name parqet misses walks 3 CDNs via
  // onError→setState, a request + re-render storm that freezes the tab. Lazy
  // defers off-screen logos to the browser's intersection observer, so only the
  // ~visible rows fetch/probe; the rest load as they scroll in. decoding="async"
  // keeps image decode off the main thread.
  return (
    <img
      src={candidates[sourceIdx]}
      alt={ticker}
      loading="lazy"
      decoding="async"
      className={cn(
        'shrink-0 bg-white object-contain p-0.5',
        sizeCls,
        radiusCls,
        className,
      )}
      onLoad={onLoaded}
      onError={onFailed}
    />
  );
}

export function SortHeader<TField extends string>({
  field,
  label,
  current,
  dir,
  onSort,
  align,
  width,
  className,
}: {
  field: TField;
  label: string;
  current: TField;
  dir: 'asc' | 'desc';
  onSort: (f: TField) => void;
  align?: 'right';
  width?: number;
  /** Extra classes, for a table that tints a group of columns. */
  className?: string;
}) {
  const active = current === field;
  return (
    // figma TableHead: h-10 px-2 text-left align-middle font-medium text-xs,
    // text-right for numeric columns; the clickable sort affordance is the
    // inner button (not the th), matching the figma markup exactly. The width
    // sets figma's column proportion (table-fixed scales it to fill).
    <th
      style={width ? { width } : undefined}
      className={cn(
        'h-10 px-2 text-left align-middle text-xs font-medium text-foreground whitespace-nowrap select-none',
        align === 'right' && 'text-right',
        className,
      )}
    >
      <button
        type="button"
        onClick={() => onSort(field)}
        className={cn(
          'flex items-center gap-1 hover:text-foreground transition-colors',
          align === 'right' && 'justify-end ml-auto',
        )}
      >
        {label}
        {/* figma sort glyph: a small downward triangle in muted/50; we rotate
            it 180° (and darken) only when this column is the active asc sort,
            keeping the exact figma glyph while preserving a direction cue. */}
        <svg
          viewBox="0 0 12 12"
          fill="currentColor"
          className={cn(
            'w-2.5 h-2.5 inline-block transition-transform',
            active ? 'text-foreground' : 'text-muted-foreground/50',
            active && dir === 'asc' && 'rotate-180',
          )}
        >
          <path d="M2 4 L6 8 L10 4 Z" />
        </svg>
      </button>
    </th>
  );
}
